import type {
  AiRequestPayload,
  AnalysisBox,
  AnalysisPoint,
  RadiometricSettings,
  Thermogram,
  ZoneMetrics
} from "./types";

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const analyzeBox = (
  thermogram: Thermogram,
  correctedValues: Float32Array,
  box: AnalysisBox,
  settings: RadiometricSettings
): ZoneMetrics => {
  const x0 = clamp(Math.floor(box.x * thermogram.width), 0, thermogram.width - 1);
  const y0 = clamp(Math.floor(box.y * thermogram.height), 0, thermogram.height - 1);
  const x1 = clamp(
    Math.ceil((box.x + box.width) * thermogram.width),
    x0 + 1,
    thermogram.width
  );
  const y1 = clamp(
    Math.ceil((box.y + box.height) * thermogram.height),
    y0 + 1,
    thermogram.height
  );

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let sum = 0;
  let sampleCount = 0;
  let hotX = x0;
  let hotY = y0;

  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const value = correctedValues[y * thermogram.width + x];
      if (!Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) {
        max = value;
        hotX = x;
        hotY = y;
      }
      sum += value;
      sampleCount += 1;
    }
  }

  const avg = sum / Math.max(1, sampleCount);
  const deltaAmbient = max - settings.ambientTempC;
  const deltaZone = max - min;

  return {
    id: box.id,
    label: box.label,
    kind: "box",
    min,
    max,
    avg,
    deltaAmbient,
    deltaZone,
    sampleCount,
    hotspot: {
      x: hotX / Math.max(1, thermogram.width - 1),
      y: hotY / Math.max(1, thermogram.height - 1),
      value: max
    },
    severity: severityFor(max, deltaAmbient, deltaZone)
  };
};

export const analyzePoint = (
  thermogram: Thermogram,
  correctedValues: Float32Array,
  point: AnalysisPoint,
  settings: RadiometricSettings
): ZoneMetrics => {
  const x = clamp(Math.round(point.x * (thermogram.width - 1)), 0, thermogram.width - 1);
  const y = clamp(
    Math.round(point.y * (thermogram.height - 1)),
    0,
    thermogram.height - 1
  );
  const value = correctedValues[y * thermogram.width + x];
  const deltaAmbient = value - settings.ambientTempC;

  return {
    id: point.id,
    label: point.label,
    kind: "point",
    min: value,
    max: value,
    avg: value,
    deltaAmbient,
    deltaZone: 0,
    sampleCount: 1,
    hotspot: {
      x: point.x,
      y: point.y,
      value
    },
    severity: severityFor(value, deltaAmbient, 0)
  };
};

// Deterministic, offline detection of the hottest connected regions in the
// thermal matrix. Returns measurement points placed on each region's peak,
// strongest first, spatially separated. Used to bootstrap the analysis (and to
// ground the AI proposal) without sending anything to an external service.
export const detectHotspots = (
  thermogram: Thermogram,
  correctedValues: Float32Array,
  settings: RadiometricSettings,
  maxCount = 5
): AnalysisPoint[] => {
  const { width, height } = thermogram;
  const total = width * height;
  if (total === 0) return [];

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (let index = 0; index < total; index += 1) {
    const value = correctedValues[index];
    if (!Number.isFinite(value)) continue;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || max - min < 0.5) return [];

  // A region must be both noticeably hotter than the scene and above ambient.
  const threshold = Math.max(min + 0.6 * (max - min), settings.ambientTempC + 8);
  const minPixels = Math.max(3, Math.round(total * 0.0004));

  const visited = new Uint8Array(total);
  const stack: number[] = [];
  const peaks: Array<{ x: number; y: number; value: number; size: number }> = [];

  for (let start = 0; start < total; start += 1) {
    if (visited[start] || correctedValues[start] < threshold) continue;
    visited[start] = 1;
    stack.length = 0;
    stack.push(start);
    let size = 0;
    let peakIndex = start;
    let peakValue = correctedValues[start];
    while (stack.length > 0) {
      const cell = stack.pop() as number;
      const value = correctedValues[cell];
      if (!Number.isFinite(value) || value < threshold) continue;
      size += 1;
      if (value > peakValue) {
        peakValue = value;
        peakIndex = cell;
      }
      const cx = cell % width;
      const cy = (cell - cx) / width;
      const neighbours = [
        cx > 0 ? cell - 1 : -1,
        cx < width - 1 ? cell + 1 : -1,
        cy > 0 ? cell - width : -1,
        cy < height - 1 ? cell + width : -1
      ];
      for (const next of neighbours) {
        if (next >= 0 && !visited[next] && correctedValues[next] >= threshold) {
          visited[next] = 1;
          stack.push(next);
        }
      }
    }
    if (size >= minPixels) {
      peaks.push({
        x: peakIndex % width,
        y: Math.floor(peakIndex / width),
        value: peakValue,
        size
      });
    }
  }

  peaks.sort((a, b) => b.value - a.value);

  const minSeparation = Math.max(4, Math.round(Math.min(width, height) * 0.06));
  const selected: typeof peaks = [];
  for (const peak of peaks) {
    if (selected.length >= maxCount) break;
    const tooClose = selected.some(
      (kept) => Math.abs(kept.x - peak.x) < minSeparation && Math.abs(kept.y - peak.y) < minSeparation
    );
    if (!tooClose) selected.push(peak);
  }

  return selected.map((peak, order) => ({
    id: `hot-${order + 1}`,
    label: `Foyer ${order + 1}`,
    x: peak.x / Math.max(1, width - 1),
    y: peak.y / Math.max(1, height - 1)
  }));
};

const severityFor = (
  max: number,
  deltaAmbient: number,
  deltaZone: number
): ZoneMetrics["severity"] => {
  if (max >= 80 || deltaAmbient >= 45 || deltaZone >= 45) return "critical";
  if (max >= 55 || deltaAmbient >= 25 || deltaZone >= 25) return "watch";
  return "normal";
};

export const severityLabel = (severity: ZoneMetrics["severity"]) => {
  const labels: Record<ZoneMetrics["severity"], string> = {
    normal: "Normal",
    watch: "A surveiller",
    critical: "Critique"
  };
  return labels[severity];
};

export const buildLocalAiSummary = (payload: AiRequestPayload) => {
  if (payload.metrics.length === 0) {
    return "Aucune zone analysee. Ajoutez une box ou un point pour generer une synthese exploitable.";
  }

  const sorted = [...payload.metrics].sort((a, b) => b.max - a.max);
  const hottest = sorted[0];
  const alerts = sorted.filter((metric) => metric.severity !== "normal");
  const confidence =
    payload.confidence >= 0.7
      ? "Le decodage parait exploitable."
      : payload.confidence >= 0.35
        ? "Le decodage doit etre confirme avant conclusion."
        : "Le decodage est un apercu non radiometrique.";

  const alertText =
    alerts.length > 0
      ? `${alerts.length} zone(s) demandent une attention prioritaire : ${alerts
          .map((metric) => `${metric.label} (${metric.max.toFixed(1)} °C)`)
          .join(", ")}.`
      : "Aucune zone ne depasse les seuils de triage internes.";

  return [
    `${confidence} Mode: ${payload.decodeMode}.`,
    `Point chaud principal: ${hottest.label} a ${hottest.max.toFixed(1)} °C, soit ${hottest.deltaAmbient.toFixed(1)} °C au-dessus de l'ambiance renseignee.`,
    alertText,
    `Reglages pris en compte: emissivite ${payload.settings.emissivity.toFixed(2)}, temperature reflechie ${payload.settings.reflectedTempC.toFixed(1)} °C, ambiance ${payload.settings.ambientTempC.toFixed(1)} °C.`,
    "Controle recommande: comparer avec la charge, l'environnement, la distance de mesure et une photo visible avant qualification Q19."
  ].join("\n");
};
